import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { fetchOrganizationUsers } from "feature/admin/actions";
import {
  selectChecked,
  selectLoadingUsers,
  selectSelectedUsers,
  selectUsers,
  toggleAll,
  toggleUserSelection,
} from "feature/admin/slice";
import { User } from "feature/admin/types";
import { useEffect, useLayoutEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import MoveUsersButton from "./components/MoveUsersButton";

export default function Users() {
  const checkbox = useRef<HTMLInputElement>(null);
  const { orgId } = useParams();
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(fetchOrganizationUsers(orgId || ""));
  }, [orgId]);

  const users = useAppSelector(selectUsers);

  const selectedUsers = useAppSelector(selectSelectedUsers);
  const checked = useAppSelector(selectChecked);
  const loadingUsers = useAppSelector(selectLoadingUsers);

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedUsers.length > 0 && selectedUsers.length < users.length;
    if (checkbox.current) checkbox.current.indeterminate = isIndeterminate;
  }, [selectedUsers, users.length]);

  const toggleAllHandler = () => {
    dispatch(toggleAll());
  };

  const toggleUserSelectionHandler = (user: User, checked: boolean) => {
    dispatch(toggleUserSelection({ user, checked }));
  };

  return (
    <>
      <div className="flex flex-col gap-6">
        <h2 className="text-lg font-semibold leading-6 text-gray-900">Users</h2>
        {loadingUsers && <p className="text-sm">Loading users...</p>}
        {!loadingUsers && users.length === 0 && (
          <p className="text-sm">No users found.</p>
        )}
        {!loadingUsers && users.length > 0 && (
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="relative">
                  {selectedUsers.length > 0 && (
                    <div className="absolute left-14 top-0 flex h-12 items-center space-x-3 bg-white sm:left-12">
                      <MoveUsersButton users={selectedUsers} />
                    </div>
                  )}
                  <table className="min-w-full table-fixed divide-y divide-gray-300">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="relative px-7 sm:w-12 sm:px-6"
                        >
                          <input
                            type="checkbox"
                            className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                            ref={checkbox}
                            checked={checked}
                            onChange={toggleAllHandler}
                          />
                        </th>
                        <th
                          scope="col"
                          className="min-w-[12rem] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900"
                        >
                          Name
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          Title
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          Email
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          Role
                        </th>
                        <th
                          scope="col"
                          className="relative py-3.5 pl-3 pr-4 sm:pr-3"
                        >
                          <span className="sr-only">Edit</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {users.map((user) => (
                        <tr
                          key={user.username}
                          className={
                            selectedUsers.includes(user)
                              ? "bg-gray-50"
                              : undefined
                          }
                        >
                          <td className="relative px-7 sm:w-12 sm:px-6">
                            {selectedUsers.includes(user) && (
                              <div className="absolute inset-y-0 left-0 w-0.5 bg-indigo-600" />
                            )}
                            <input
                              type="checkbox"
                              className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                              value={user.email_address}
                              checked={selectedUsers.includes(user)}
                              onChange={(e) =>
                                toggleUserSelectionHandler(
                                  user,
                                  e.target.checked
                                )
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap py-5 pl-4 pr-3 text-sm sm:pl-0">
                            <div className="flex items-center">
                              <div>
                                <div className="font-medium text-gray-900">{`${user.firstname} ${user.lastname}`}</div>
                                <div className="mt-1 text-gray-500">
                                  {user.email_address}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                            <div className="text-gray-900">title</div>
                            <div className="mt-1 text-gray-500">department</div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                            <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                              Active
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                            role
                          </td>
                          <td className="relative whitespace-nowrap py-5 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                            <a
                              href="#"
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              Edit<span className="sr-only"></span>
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
